import {useEffect, useState} from 'react';
import {animate, dynamic, isServer, toIntent} from '@sail/engine';
import {tokens as $} from '@sail/tokens';
import {css} from '../css';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';
import {useShouldDisplayBottomSheet} from '../components/bottom-sheet/useShouldDisplayBottomSheet';
import {BOTTOM_SHEET_ANIMATION_EASING} from '../components/bottom-sheet/BottomSheet.constants';

const visualViewport = !isServer ? window.visualViewport : undefined;

type ViewportSize = {
  width: number;
  height: number;
  offsetTop: number;
};

function getViewportSize(): ViewportSize {
  return {
    // eslint-disable-next-line @sail/ui-no-reflow
    width: visualViewport?.width || window.innerWidth,
    // eslint-disable-next-line @sail/ui-no-reflow
    height: visualViewport?.height || window.innerHeight,
    // eslint-disable-next-line @sail/ui-no-reflow
    offsetTop: visualViewport?.offsetTop || 0,
  };
}

// Use visualViewport api to track viewport available height when iOS virtual keyboard is open
function useViewportArea(): ViewportSize {
  const [size, setSize] = useState(() =>
    isServer ? {width: 0, height: 0, offsetTop: 0} : getViewportSize(),
  );

  useEffect(() => {
    const onResize = () => {
      setSize(getViewportSize());
    };

    visualViewport?.addEventListener('resize', onResize);
    visualViewport?.addEventListener('scroll', onResize);

    return () => {
      visualViewport?.removeEventListener('resize', onResize);
      visualViewport?.removeEventListener('scroll', onResize);
    };
  }, []);

  return size;
}

export type MobileDisplay = 'bottom-sheet' | 'fullscreen-sheet';

function useViewportAndKeyboardHeightStyles() {
  // eslint-disable-next-line @sail/ui-no-reflow
  const {height, offsetTop} = useViewportArea();

  /**
   * The way to get the exact keyboard height is:
   * window.innerHeight (height of the screen) - offsetTop (the amount the viewport has been scrolled up) - height (the height of the viewport)
   *
   * If we are in an iframe (Connect) we set this as 0px.
   * Currently, the safe-area-insets-* env vars are not consistently applied to iframes across browsers: https://github.com/w3c/csswg-drafts/issues/4670
   * So Connect set the padding themselves: https://git.corp.stripe.com/stripe-internal/mint/blob/master/pay-server/frontend/connect/submerchant-surfaces/src/ui-layer-frame/utils/MobileUIOverrides.tsx#L74
   */
  const keyboardHeight =
    !isServer && window.parent === window
      ? Math.max(
          // eslint-disable-next-line @sail/ui-no-reflow
          window.innerHeight - offsetTop - height,
          0,
        )
      : 0;

  return css({
    '--viewport-height': '100vh',
    '@supports (height: 100dvh)': {
      '--viewport-height': '100dvh',
    },
    paddingBottom: `max(${keyboardHeight}px, env(safe-area-inset-bottom, 0px))`,
    transition: 'padding-bottom 50ms ease-out',
  });
}

export const bottomSheetStyles = dynamic(() => {
  const viewportAndKeyboardHeightStyles = useViewportAndKeyboardHeightStyles();

  return [
    viewportAndKeyboardHeightStyles,
    css({
      position: 'fixed',
      bottom: 'var(--sail-layer-margin-bottom, 0px)',
      top: 'initial',
      right: 'initial',
      left: 'initial',
      width: '100vw',
      borderWidth: '0px',
      borderBottomLeftRadius: '0px',
      borderBottomRightRadius: '0px',
      borderTopLeftRadius: $['space.150'],
      borderTopRightRadius: $['space.150'],
      minHeight:
        'max(calc(var(--viewport-height) * 0.25), var(--close-confirmation-dialog-height, 0px))',
      maxHeight:
        'calc(var(--viewport-height) - env(safe-area-inset-bottom, 0) - env(safe-area-inset-top, 0) - 40px)',
      boxShadow: 'overlay.inverted',
      transform: 'translateY(0%)',
      backgroundColor: 'surface',
    }),
  ];
});

export const fullscreenSheetStyles = dynamic(() => {
  const viewportAndKeyboardHeightStyles = useViewportAndKeyboardHeightStyles();
  const {isSmallScreen} = useMediaQuery();

  const styles = css({
    width: 'fill',
    minHeight: 'var(--close-confirmation-dialog-height, 0px)',
    height:
      'calc(100% - var(--sail-layer-margin-top, 0px) - var(--sail-layer-margin-bottom, 0px))',
    borderRadius: '0px',
    maxHeight: 'fill',
    borderWidth: '0px',
    top: 'var(--sail-layer-margin-top, 0px)',
    boxShadow: 'none',
  });

  // This always checks for `dialog`, even though fullscreenSheetStyles is also used on Drawers.
  // But since they're feature flaged behind the same flag, it doesn't matter.
  const shouldDisplayBottomSheet = useShouldDisplayBottomSheet('dialog');

  if (!isSmallScreen) {
    return [];
  }

  if (!shouldDisplayBottomSheet) {
    // Temporarily disable viewportAndKeyboardHeightStyles for non-bottom sheet overlays
    // This can be removed once bottom sheets are GA, and usage of viewportAndKeyboardHeightStyles is removed from desktop implementations.
    return [styles];
  }

  return [viewportAndKeyboardHeightStyles, styles];
});

export const fullscreenAnimation = (disabled: boolean) =>
  animate.inout({
    duration: 320,
    transform: 'translateX(100%)',
    easing: BOTTOM_SHEET_ANIMATION_EASING,
    preserveEasingOnReverse: true,
    disabled,
  });

export const bottomSheetAnimation = (
  disabled: boolean,
  duration: number,
  hasChildSheets: boolean,
) =>
  toIntent([
    animate
      .transition({
        duration,
        transform: `translateY(calc(100% + var(--sail-layer-margin-bottom, 0px))) scale(0.9)`,
        easing: BOTTOM_SHEET_ANIMATION_EASING,
        preserveEasingOnReverse: true,
        disabled,
      })
      .when(hasChildSheets),
    animate.inout({
      duration,
      transform:
        'translateY(calc(100% + var(--sail-layer-margin-bottom, 0px)))',
      easing: BOTTOM_SHEET_ANIMATION_EASING,
      preserveEasingOnReverse: true,
      disabled,
    }),
  ]);
